import {AfterViewInit, Component, OnInit} from '@angular/core';
import {User} from '../../../dto/user';
import {ReplaySubject, Subject} from 'rxjs';
import {FormControl} from '@angular/forms';
import {Market} from '../../../dto/market';
import {MarketLevel} from '../../../dto/market-level';
import {UserRole} from '../../../dto/user-role';
import {ActivatedRoute} from '@angular/router';
import {BaseService} from '../../../services/base.service';
import {AuthService} from '../../../services/auth.service';
import {takeUntil} from 'rxjs/internal/operators';
import {MatSelectChange} from '@angular/material';
import {ChemistLocation} from '../../../dto/chemist-location';
import {DoctorLocation} from '../../../dto/doctor-location';
import {GeneralBroadcastService} from '../../../services/general-broadcast.service';
import * as L from 'leaflet';
import {DivIcon, Icon, MarkerOptions} from 'leaflet';

@Component({
    selector: 'app-doctor-chemist-location',
    templateUrl: './doctor-chemist-location.component.html',
    styleUrls: ['./doctor-chemist-location.component.scss']
})
export class DoctorChemistLocationComponent implements OnInit, AfterViewInit {

    lat = 23.8383283;
    lng = 90.3759853;

    loggedUser: User;
    userList: User[] = [];
    filteredUserList: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
    userFilter: FormControl = new FormControl();
    chemistLocationList: ChemistLocation[] = [];
    doctorLocationList: DoctorLocation[] = [];
    loadingData = false;
    markets = [];
    selectedMarket: Market[] = [];
    marketLevels: MarketLevel[] = [];
    userRoles: UserRole[];

    showDoctors = new FormControl(false);
    showChemists = new FormControl(false);
    filters = {
        'MarketIdList': null,
        'ShowChemist': true,
        'ShowDoctor': true
    };

    userType = 'ORGANIZATION_USER';

    markerClusterGroupForDoctor = L.markerClusterGroup({
        iconCreateFunction: (cluster): DivIcon => {
            return L.divIcon({
                html: `<div style="background:rgba(17,156,231,.8);
                        border-radius:50%;
                        width:40px;
                        height:40px;
                        display:flex;
                        justify-content:center;
                        align-items:center;
                        color:#fff;
                        font-weight:bold;">
                        ${cluster.getChildCount()}
                   </div>`,
                className: '',
                iconSize: L.point(40, 40)
            });
        }
    });

    markerClusterGroupForChemist = L.markerClusterGroup({
        iconCreateFunction: (cluster): DivIcon => {
            return L.divIcon({
                html: `<div style="background:rgba(110,204,57,.8);
                        border-radius:50%;
                        width:40px;
                        height:40px;
                        display:flex;
                        justify-content:center;
                        align-items:center;
                        color:#fff;
                        font-weight:bold;">
                        ${cluster.getChildCount()}
                   </div>`,
                className: '',
                iconSize: L.point(40, 40)
            });
        }
    });
    map: any;
    customStyle = [
        {
            'featureType': 'landscape',
            elementType: 'geometry',
            'stylers': [
                {'hue': '#37cb37'},
                {'saturation': -20},
                {'lightness': -8}
            ]
        }
    ];
    private _unsubscribeAll: Subject<any>;

    constructor(private route: ActivatedRoute, private service: BaseService, private auth: AuthService, private generalBroadcastService: GeneralBroadcastService) {
        this._unsubscribeAll = new Subject();
        this.generalBroadcastService.broadcastTitle('Doctor/Chemist Location');
        this.generalBroadcastService.broadcastSelectedModuleId('LOCATION');
        this.loggedUser = auth.user;
        this.marketLevels = this.loggedUser.MarketLevelHierarchy;
        this.marketLevels.forEach(marketLevel => {
            this.markets[marketLevel.Rank] = [];
            this.selectedMarket[marketLevel.Rank] = null;
        });
    }

    ngOnInit(): void {
        this.map = L.map('map').setView([23.8383283, 90.3759853], 7);

        L.gridLayer.googleMutant({
            type: 'roadmap',
            styles: this.customStyle
        }).addTo(this.map);

        this.service.getUserRoleList({}).subscribe(pUserRoles => this.userRoles = pUserRoles);
        this.userFilter.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            if (!this.userList) {
                return;
            }

            const search = this.userFilter.value;
            if (!search) {
                this.filteredUserList.next(this.userList.slice());
                return;
            }

            this.filteredUserList.next(this.userList.filter(user => user.Name.toLowerCase().indexOf(search.toLowerCase()) > -1 || user.Code.toLowerCase().indexOf(search.toLowerCase()) > -1));
        });
    }

    ngAfterViewInit(): void {
        this.service.getMarketList({
            MarketLevelId: this.marketLevels[0].Id,
            Active: true
        }).subscribe(marketListResponse => {
            this.markets[1] = marketListResponse;
        });
    }

    getUserList(): void {
        let marketIdList = null;
        for (let i = this.selectedMarket.length - 1; i >= 0; i--) {
            if (this.selectedMarket[i]) {
                marketIdList = [this.selectedMarket[i].Id];
                break;
            }
        }

        this.service.getUserList({
            ResponseFields: 'Id,Code,Name,Active',
            UserType: this.userType,
            MarketIdList: marketIdList
        }).subscribe(userList => {
            this.userList = userList;
            this.filteredUserList.next(this.userList.slice());
        });
    }

    _resetChildDropdowns(pParentRank: number): void {
        this.marketLevels.forEach(marketLevel => {
            if (marketLevel.Rank > pParentRank) {
                this.selectedMarket[marketLevel.Rank] = null;
                this.markets[marketLevel.Rank] = [];
            }
        });
    }

    populateChildrenMarkets(event: MatSelectChange): void {
        const market: Market = event.value;
        if (market) {
            this._resetChildDropdowns(market.MarketLevel.Rank);
            if (this.marketLevels[market.MarketLevel.Rank]) {
                this.service.getMarketList({
                    MarketLevelId: this.marketLevels[market.MarketLevel.Rank].Id,
                    ParentMarketId: market.Id,
                    Active: true
                }).subscribe(marketListResponse => {
                    this.markets[market.MarketLevel.Rank + 1] = marketListResponse;
                });
            }
        } else {
            this._resetChildDropdowns(parseInt(event.source.id.slice(6), 10));
        }
    }

    getLocationList(): void {
        this.loadingData = true;
        this.filters.MarketIdList = null;

        for (let i = this.selectedMarket.length - 1; i >= 0; i--) {
            if (this.selectedMarket[i]) {
                this.filters.MarketIdList = [this.selectedMarket[i].Id];
                break;
            }
        }

        const doctorIcon: MarkerOptions = {
            icon: new Icon({
                iconUrl: '/assets/icons/location-icons/stethescope.svg',
                iconSize: [45, 50],
                iconAnchor: [30, 45]
            })
        };

        const chemistIcon: MarkerOptions = {
            icon: new Icon({
                iconUrl: '/assets/icons/location-icons/house.svg',
                iconSize: [45, 50],
                iconAnchor: [30, 45]
            })
        };

        this.markerClusterGroupForDoctor.clearLayers();
        this.markerClusterGroupForChemist.clearLayers();

        this.service.getChemistAndDoctorLocationList(this.filters).subscribe(
            locationList => {

                if (this.filters.ShowChemist && locationList['ChemistList'] && locationList['ChemistList'].length > 0) {

                    locationList['ChemistList'].forEach(pData => {

                        if ((pData.Location.Latitude > 0 && pData.Location.Latitude <= 90)
                            && (pData.Location.Longitude > 0 && pData.Location.Longitude <= 180)) {

                            const marker = L.marker(
                                [
                                    pData.Location.Latitude,
                                    pData.Location.Longitude
                                ],
                                chemistIcon
                            ).bindPopup(
                                '<div style="font-size:13px;font-weight:700;font-family:Muli;">'
                                + pData.Chemist.Name + ' (' + pData.Chemist.Code + ')' +
                                '</div>' +

                                '<div style="font-size:13px;font-weight:700;font-family:Muli;">'
                                + 'Latitude: ' + pData.Location.Latitude +
                                '</div>' +

                                '<div style="font-size:13px;font-weight:700;font-family:Muli;">'
                                + 'Longitude: ' + pData.Location.Longitude +
                                '</div>'
                            );

                            this.markerClusterGroupForChemist.addLayer(marker);
                        }

                    });

                }

                if (this.filters.ShowDoctor && locationList['DoctorList'] && locationList['DoctorList'].length > 0) {

                    locationList['DoctorList'].forEach(pData => {

                        if ((pData.Location.Latitude > 0 && pData.Location.Latitude <= 90)
                            && (pData.Location.Longitude > 0 && pData.Location.Longitude <= 180)) {

                            const marker = L.marker(
                                [
                                    pData.Location.Latitude,
                                    pData.Location.Longitude
                                ],
                                doctorIcon
                            ).bindPopup(
                                '<div style="font-size:13px;font-weight:700; font-family:Muli;">'
                                + pData.Doctor.Name + ' (' + pData.Doctor.Code + ')' +
                                '</div>' +

                                '<div style="font-size:13px;font-weight:700;font-family:Muli;">'
                                + 'Latitude: ' + pData.Location.Latitude +
                                '</div>' +

                                '<div style="font-size:13px;font-weight:700;font-family:Muli;">'
                                + 'Longitude: ' + pData.Location.Longitude +
                                '</div>'
                            );

                            this.markerClusterGroupForDoctor.addLayer(marker);
                        }

                    });

                }

                this.map.addLayer(this.markerClusterGroupForDoctor);
                this.map.addLayer(this.markerClusterGroupForChemist);

                this.loadingData = false;

            },
            () => {
                this.loadingData = false;
            }
        );
    }
}
